import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  DATE_FORMAT,
  multiSort,
  sortTimestampAsc,
  toMoment,
} from '@principle-theorem/shared';
import { uniqWith } from 'lodash';
import type * as moment from 'moment-timezone';
import { type Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IScheduleSummaryEventable } from '@principle-theorem/principle-core/interfaces';
import { Event } from '@principle-theorem/principle-core';
import { GapStoreService } from '@principle-theorem/ng-principle-shared';

@Component({
  selector: 'pr-gap-list',
  templateUrl: './gap-list.component.html',
  styleUrls: ['./gap-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GapListComponent {
  private _expanded?: string;
  gaps$: ReplaySubject<IScheduleSummaryEventable[]> = new ReplaySubject(1);
  sections$: Observable<ISection[]>;
  readonly dateFormat = DATE_FORMAT;

  @Input()
  set gaps(gaps: IScheduleSummaryEventable[]) {
    if (gaps) {
      this.gaps$.next(gaps);
    }
  }

  constructor(private _gapStore: GapStoreService) {
    this.initSections$();
  }

  initSections$(): void {
    this.sections$ = this.gaps$.pipe(
      map((gaps) => gaps.map((gap) => toMoment(gap.event.from))),
      map((dates) => uniqWith(dates, (a, b) => a.isSame(b, 'day'))),
      map((dates) => {
        return dates.map((date: moment.Moment) => {
          return {
            date,
            gaps$: this.filterGapsByDate$(date),
          };
        });
      }),
      multiSort((sectionA, sectionB) =>
        sortTimestampAsc(sectionA.date, sectionB.date)
      )
    );
  }

  filterGapsByDate$(
    date: moment.Moment
  ): Observable<IScheduleSummaryEventable[]> {
    return this.gaps$.pipe(
      map((gaps) =>
        gaps.filter((gap) => {
          return toMoment(gap.event.from).isSame(date, 'day');
        })
      )
    );
  }

  isExpanded(gap: IScheduleSummaryEventable): boolean {
    return this._expanded === getGapUid(gap);
  }

  setExpanded(gap: IScheduleSummaryEventable): void {
    this._gapStore.setSelectedGap(gap);
    this._expanded = getGapUid(gap);
  }
}

function getGapUid(gap: IScheduleSummaryEventable): string {
  return gap.event.from.toDate().toString() + Event.staff(gap.event)[0].ref.id;
}

interface ISection {
  date: moment.Moment;
  gaps$: Observable<IScheduleSummaryEventable[]>;
}
